import { Box, Stack, Typography } from "@mui/material";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import Article from "../../components/Article";
import { useTenant } from "../../contexts/TenantContext";
import { gradientFillSx, gradientTextSx, tint } from "../../utils/textStyles";
import { initialsOf } from "../../utils/nameUtils";
import { isDarkTheme, isNeonTheme } from "../../utils/themeTraits";

// The light accent wash each contact icon sits in, with the glyph in the accent itself.
const badgeSx = (accent) => ({
  backgroundColor: tint(accent, 0.12),
  border: `1px solid ${tint(accent, 0.3)}`,
  color: accent,
});

const ContactRow = ({ icon, label, value, accent, textColor }) => (
  <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...badgeSx(accent),
      }}
    >
      {icon}
    </Box>

    <Stack minWidth={0}>
      <Typography fontSize={11} textTransform="uppercase" letterSpacing={0.6}
        sx={{ color: tint(textColor, 0.6) }}>
        {label}
      </Typography>
      <Typography fontSize={15} fontWeight={600} noWrap sx={{ color: textColor }}>
        {value || "—"}
      </Typography>
    </Stack>
  </Stack>
);

/**
 * Who is logged in: the initials disc, the name, and the two ways the shop reaches them.
 *
 * On a dark theme the disc is painted with the same gradient the section
 * headings are lettered in, so the page opens on the tenant's colour rather than on a form.
 * Paper keeps its strong brand colour for what can be clicked: the disc is the contact icons'
 * light badge, only larger, so the card has one kind of circle rather than two, and the name
 * is ink like the headings.
 */
const ProfileSummary = ({ user }) => {
  const { displayConfig } = useTenant();
  const isDark = isDarkTheme(displayConfig);
  const accent = displayConfig?.primaryColor;
  const textColor = displayConfig?.color;
  const discSx = isDark
    ? {
        ...gradientFillSx(displayConfig, displayConfig?.buttonColor, { hover: false }),
        // The coloured glow under it is neon; the plain dark theme sets it on a black shadow.
        boxShadow: `0 12px 30px -14px ${tint(isNeonTheme(displayConfig) ? displayConfig?.buttonColor : "#000", 0.9)}`,
      }
    : badgeSx(accent);
  const discLetterColor = isDark ? displayConfig?.buttonTextColor : accent;

  return (
    <Article alignItems="stretch" xsHorizontalPadding={3} width="100%" sx={{ py: { xs: 3, sm: 4 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 2.5, sm: 4 }}
        alignItems="center"
        justifyContent={{ xs: "center", sm: "flex-start" }}
      >
        <Box
          sx={{
            width: 84,
            height: 84,
            borderRadius: "50%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ...discSx,
          }}
        >
          {initialsOf(user?.name)
            ? (
              <Typography fontSize={30} fontWeight={800} sx={{ color: discLetterColor }}>
                {initialsOf(user?.name)}
              </Typography>
            )
            : <PersonOutlineIcon sx={{ fontSize: 40, color: discLetterColor }} />}
        </Box>

        <Stack spacing={2} minWidth={0} flex={1} alignItems={{ xs: "center", sm: "flex-start" }}>
          <Box textAlign={{ xs: "center", sm: "left" }} minWidth={0}>
            <Typography fontSize={12} textTransform="uppercase" letterSpacing={1}
              sx={{ color: tint(textColor, 0.55) }}>
              Profil
            </Typography>
            <Typography variant="h5" fontWeight={800}
              sx={gradientTextSx(displayConfig, isDark ? accent : textColor)}>
              {user?.name}
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 1.5, md: 4 }}
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <ContactRow
              icon={<EmailOutlinedIcon fontSize="small" />}
              label="Email"
              value={user?.email}
              accent={accent}
              textColor={textColor}
            />
            <ContactRow
              icon={<PhoneIphoneIcon fontSize="small" />}
              label="Telefonszám"
              value={user?.phoneNumber}
              accent={accent}
              textColor={textColor}
            />
          </Stack>
        </Stack>
      </Stack>
    </Article>
  );
};

export default ProfileSummary;
